import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { SectionHeader } from '../components/UI';
import { fmtM, calcEstadisticaProveedores } from '../utils';

const COLORS = ['#1A4E76','#1A6B45','#8B4513','#6C3483','#B03A2E','#0E6655','#7A560E','#2D7AB5'];

export default function EstadisticasProveedores({ store }) {
  const { compras, movimientos } = store;
  const [filtro, setFiltro] = useState('');
  const stats = useMemo(() => calcEstadisticaProveedores(compras, movimientos), [compras, movimientos]);
  const grandTotal = stats.reduce((a, s) => a + s.total, 0);
  const filtered = filtro ? stats.filter(s => s.nombre.toLowerCase().includes(filtro.toLowerCase())) : stats;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '14px 16px', flex: 1 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>Total compras acumuladas</div>
          <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'Courier, monospace', color: 'var(--red)' }}>{fmtM(grandTotal)}</div>
          <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 3 }}>{stats.length} proveedor{stats.length !== 1 ? 'es' : ''}</div>
        </div>
        {stats.length > 0 && (
          <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '14px 16px', flex: 1 }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>Proveedor principal</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--tx)' }}>{stats[0].nombre}</div>
            <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 3 }}>{fmtM(stats[0].total)} — {stats[0].pct.toFixed(1)}% del total</div>
          </div>
        )}
      </div>
      {stats.length > 0 && (
        <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 12 }}>Distribución por proveedor</div>
          <ResponsiveContainer width="100%" height={Math.max(120, stats.length * 36)}>
            <BarChart data={stats} layout="vertical" margin={{ left: 10, right: 80 }}>
              <XAxis type="number" tick={{ fontSize: 9, fill: 'var(--tx3)' }} tickFormatter={v => fmtM(v)} />
              <YAxis type="category" dataKey="nombre" tick={{ fontSize: 11, fill: 'var(--tx2)' }} width={100} />
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
              <Tooltip formatter={v => [fmtM(v), 'Total compras']} contentStyle={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }} />
              <Bar dataKey="total" radius={[0, 4, 4, 0]}
                label={{ position: 'right', formatter: v => grandTotal > 0 ? ((v/grandTotal)*100).toFixed(1)+'%' : '', fontSize: 10, fill: 'var(--tx3)' }}>
                {stats.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <input value={filtro} onChange={e => setFiltro(e.target.value)} placeholder="Buscar proveedor..."
        style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 12, background: 'var(--surf)', color: 'var(--tx)', outline: 'none', marginBottom: 12 }} />
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--surf2)' }}>
              {['#','Proveedor','Compras','Monto total','% del total',''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '9px 13px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0
              ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--tx3)' }}>Sin compras registradas</td></tr>
              : filtered.map((s, i) => (
                <tr key={s.nombre} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', color: 'var(--tx3)', fontSize: 11 }}>{i+1}</td>
                  <td style={{ padding: '9px 13px', fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: COLORS[i % COLORS.length], flexShrink: 0 }}></div>
                      {s.nombre}
                    </div>
                  </td>
                  <td style={{ padding: '9px 13px', color: 'var(--tx3)' }}>{s.count} compra{s.count !== 1 ? 's' : ''}</td>
                  <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--red)' }}>{fmtM(s.total)}</td>
                  <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontWeight: 700 }}>{s.pct.toFixed(1)}%</td>
                  <td style={{ padding: '9px 13px', minWidth: 120 }}>
                    <div style={{ background: 'var(--surf3)', borderRadius: 99, height: 6, overflow: 'hidden' }}>
                      <div style={{ width: s.pct+'%', height: '100%', borderRadius: 99, background: COLORS[i % COLORS.length] }}></div>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
